<template>
  <Grid
    align-items="center"
    gap="var(--r-space-md)"
    class="bulk-update-actions"
    :class="{ 'has-na': selectedCol && selectedCol.na }"
  >
    <Grid justify-content="end" class="bulk-update-text">
      <div class="text-heading-3 text-secondary-1 q-pr-sm">Bulk update</div>
      <div class="text-secondary-1" :class="!!numberOfSelectedRows ? 'text-body-3' : 'text-body-1 q-py-xs'">
        {{ numberOfSelectedRows }} rows selected
      </div>
    </Grid>

    <RDropdown
      v-model="selectedCol"
      :options="editableColumns"
      :disable="updateLoading"
      class="column-dropdown"
      no-label
      no-hint
      placeholder="Select column to edit"
    />

    <RCheckbox
      v-if="selectedCol && selectedCol.na"
      v-model="na"
      :disable="updateLoading"
      label="N/A"
      style="align-self: center"
      @hook:created="na = false"
    />

    <div class="fields relative-position">
      <div v-if="!!na" class="fake-readonly bg-white-1 cursor-pointer" @click="na = false">
        <LmTooltip anchor="top middle" self="bottom middle">
          <div class="text-body-1">Click to enable the field</div>
        </LmTooltip>
      </div>

      <RInput
        v-if="!selectedCol || (selectedCol && selectedCol.field === 'text')"
        v-model="value"
        :disable="updateLoading"
        :placeholder="selectedCol && selectedCol.placeholder"
        :class="{ invisible: !(selectedCol && selectedCol.field === 'text') }"
        no-feedback-icon
        no-hint
        no-label
      />

      <RInput
        v-if="selectedCol && selectedCol.field === 'date'"
        v-model="value"
        :disable="updateLoading"
        is-date
        no-feedback-icon
        no-hint
        no-label
      />

      <RInput
        v-if="selectedCol && selectedCol.field === 'number'"
        ref="numberInput"
        :disable="updateLoading"
        :value="value"
        :placeholder="selectedCol.placeholder"
        on-clear="focus"
        no-feedback-icon
        no-hint
        no-label
        @clear="onClearNumberInput"
      />

      <RDropdown
        v-if="selectedCol && selectedCol.field === 'dropdown'"
        :key="selectedCol.value"
        v-model="value"
        :disable="updateLoading"
        :placeholder="selectedCol.placeholder"
        :options="selectedCol.options"
        :multiple="selectedCol.multiple"
        :emit-value="selectedCol.multiple"
        :no-options-sort="selectedCol.noSort"
        no-label
        no-hint
        option-value="id"
        option-label="name"
      />

      <Grid
        v-if="selectedCol && selectedCol.field === 'checkbox'"
        gaf="column"
        justify-content="center"
        align-items="center"
        gap="var(--r-space-sm)"
        class="full-height"
      >
        <RCheckbox v-model="value" :true-value="true" :false-value="false" label="Yes" />
        <RCheckbox v-model="value" :true-value="false" :false-value="true" label="No" />
      </Grid>
    </div>

    <div>
      <RBtn
        :class="{ invisible: !selectedCol }"
        :disable="disableUpdate || !numberOfSelectedRows"
        :loading="updateLoading"
        type="tertiary"
        size="small"
        style="align-self: center"
        @click="update"
      >
        Update
      </RBtn>
      <LmTooltip v-if="!numberOfSelectedRows" anchor="top middle" self="bottom middle">
        <div class="text-body-1">No rows have been selected</div>
      </LmTooltip>
    </div>
  </Grid>
</template>

<script>
import * as apiTo from '@/modular-api'
// Libraries
import { mapGetters, mapState } from 'vuex'
import Cleave from 'cleave.js'

export default {
  props: {
    agGrid: Object
  },

  data: () => ({
    selectedCol: null,
    na: false,
    value: null,
    rawValue: null,
    updateLoading: false
  }),

  computed: {
    ...mapState('lv-subscriptions', ['productTypes', 'publishers', 'stakeholders', 'departments', 'metadataColumns']),

    ...mapState('lv-subscriptions-edit-mode', ['rowsSelected', 'selectAll', 'tableState']),
    ...mapGetters('lv-subscriptions-edit-mode', ['numberOfSelectedRows']),

    ...mapGetters('subscriptions/subscriptionPeriods', ['vendors', 'owners']),

    editableColumns() {
      return [
        {
          label: 'Product type',
          value: 'product_type',
          na: true,
          field: 'dropdown',
          placeholder: 'Select Product type',
          options: this.productTypes
        },
        {
          label: 'Publisher',
          value: 'publisher',
          na: false,
          field: 'dropdown',
          placeholder: 'Select publisher',
          options: this.publishers
        },
        {
          label: 'Vendor',
          value: 'vendor',
          na: true,
          field: 'dropdown',
          placeholder: 'Select vendor',
          options: this.vendors
        },
        { label: 'Detail', value: 'detail', na: true, field: 'text', placeholder: 'Enter new detail' },
        {
          label: 'License manager',
          value: 'owner',
          na: false,
          field: 'dropdown',
          placeholder: 'Select license manager',
          options: this.owners
        },
        {
          label: 'Decision deadline',
          value: 'cancellation_deadline',
          na: true,
          field: 'date',
          placeholder: 'Select decision deadline'
        },
        {
          label: 'Publisher reference',
          value: 'publisher_reference',
          na: true,
          field: 'text',
          placeholder: 'Enter new publisher reference'
        },
        {
          label: 'Library reference',
          value: 'library_reference',
          na: true,
          field: 'text',
          placeholder: 'Enter new library reference'
        },
        { label: 'Usage', value: 'usage', na: true, field: 'number', placeholder: 'Enter new usage' },
        {
          label: 'Stakeholders',
          value: 'stakeholders',
          na: true,
          field: 'dropdown',
          placeholder: 'Select stakeholders',
          options: this.stakeholders,
          multiple: true
        },
        {
          label: 'Departments',
          value: 'departments',
          na: true,
          field: 'dropdown',
          placeholder: 'Select departments',
          options: this.departments,
          multiple: true
        },

        ...(this.metadataColumns || []).reduce((acc, curr) => {
          acc.push({
            label: curr.name,
            value: `meta_data.${curr.system_name}`,
            na: !curr.is_required,
            ...this.getMetadataProperties(curr)
          })

          return acc
        }, [])
      ]
    },

    disableUpdate() {
      let value

      if (Array.isArray(this.value)) value = this.value.length || null
      else if (typeof this.value === 'string') value = this.value.trim() || null
      else value = this.value

      return value === null && !this.na
    }
  },

  watch: {
    selectedCol(col) {
      this.na = false
      this.value = col?.multiple ? [] : null
      this.rawValue = null
      if (col?.field === 'number') this.setCleaveFormatting(col.cleaveOptions ?? {})
    },

    rowsSelected(newVal) {
      if (!newVal.length) {
        // reset when selection gets cleared
        this.selectedCol = null
        this.na = false
        this.value = null
        this.rawValue = null
      }
    },

    value() {
      this.na = false
    }
  },

  methods: {
    thereIsUnsavedChanges() {
      // access from parent
      return this.value || this.na
    },

    getInfo() {
      // access from parent
      const rowsText = this.rowsSelected.length === 1 ? '1 row' : `${this.rowsSelected.length} rows`
      const col = this.selectedCol.label

      return { rowsText, col }
    },

    getMetadataProperties(metadata) {
      const properties = {}

      if (metadata.type.name === 'select') {
        properties.field = 'dropdown'
        properties.multiple = true
        properties.options = [...metadata.value_options]
        properties.placeholder = `Select ${metadata.name}`
      } else if (['text', 'textarea'].includes(metadata.type.name)) {
        properties.field = 'text'
        properties.placeholder = `Enter ${metadata.name}`
      } else if (metadata.type.name === 'checkbox') {
        properties.field = 'dropdown'
        properties.options = [
          { name: 'Yes', id: true },
          { name: 'No', id: false }
        ]
        properties.noSort = true
        properties.placeholder = `Select ${metadata.name}`
      }

      return properties
    },

    setCleaveFormatting(cleaveOptions) {
      this.$nextTick(() => {
        const delimiter = this.settings.thousand_separator
        const valueChanged = ({ target: { rawValue, value } }) => {
          this.rawValue = rawValue
          this.value = value
        }

        this.cleave = new Cleave(this.$refs.numberInput.$el.querySelector('input'), {
          numeral: true,
          numeralDecimalMark: this.settings.decimal_separator,
          delimiter: delimiter === 'space' ? ' ' : delimiter,
          numeralDecimalScale: cleaveOptions.numeralDecimalScale ?? 2,
          onValueChanged: valueChanged
        })

        this.cleave.setRawValue(this.value)
      })
    },

    onClearNumberInput() {
      this.cleave.setRawValue(null)
    },

    getMetadataPayload() {
      const col = this.selectedCol
      const system_name = col.value.split('.')[1]
      const metadata = this.metadataColumns.find(m => m.system_name === system_name)
      let values = this.value

      if (this.na) values = col.multiple ? [] : null
      else if (col.field === 'dropdown') values = col.multiple ? [...this.value] : this.value?.id

      return { id: metadata.id, is_required: metadata.is_required, values }
    },

    getValueForBE() {
      if (this.selectedCol.value.startsWith('meta_data')) return this.getMetadataPayload()
      if (this.na) return this.selectedCol.multiple ? [] : null

      const col = this.selectedCol

      if (col.field === 'dropdown' && !col.multiple) return this.value.id
      if (col.field === 'date') return this.formatDateForPost(this.value, this.settings.date_format)
      if (col.field === 'number') return Number(this.rawValue)

      return this.value
    },

    update(emit = true) {
      const value = this.getValueForBE()
      const rowsIds = this.rowsSelected.map(v => v.id)
      const ids = this.selectAll ? 'excluded_ids' : 'included_ids'
      const field = this.selectedCol.value.startsWith('meta_data') ? 'meta_data' : this.selectedCol.value

      this.updateLoading = true

      return apiTo
        .BULK_UPDATE_SUB_PERIODS(
          {
            [ids]: rowsIds,
            [field]: value,
            filterModel: this.agGrid.api.getFilterModel()
          },
          this.tableState.chipFilter
        )
        .then(() => emit && this.$emit('bulk-update'))
        .catch(() => {})
        .finally(() => (this.updateLoading = false))
    }
  }
}
</script>

<style lang="scss" scoped>
.bulk-update-actions {
  --input-min-width: 150px;
  --input-max-width: 215px;
  --min-max: minmax(var(--input-min-width), var(--input-max-width));

  grid-template-columns: auto var(--min-max) var(--min-max) auto;
  justify-content: end;

  &.has-na {
    grid-template-columns: auto var(--min-max) auto var(--min-max) auto;
  }

  .bulk-update-text {
    white-space: nowrap;
  }

  .fields {
    > .fake-readonly {
      position: absolute;
      width: 100%;
      height: 100%;
      z-index: 1;
      padding: var(--r-space-xs);
      box-sizing: content-box;
      transform: translate(calc(var(--r-space-xs) * -1), calc(var(--r-space-xs) * -1));
      top: 0;
      left: 0;
      opacity: 0.6;
    }

    > * {
      width: 100%;
    }
  }
}

@media (max-width: 1260px) {
  .bulk-update-actions {
    justify-content: start;
  }
}
</style>
